import { AppStateStatus } from 'react-native';
import { HubConnection } from '@microsoft/signalr';
import { createSlice, createAction } from '@reduxjs/toolkit';
import { State } from '../state.types';
import {
  AppState,
  PushNotificationClick,
  PushNotificationImpression,
  RegisterUserPayload,
  SetFirstNamePayload,
} from './app.types';
import { AsyncStorage } from '../../storage';
import { persistReducer } from '../persist/persist';

export const appOpened = createAction('app/appOpened');
export const appAuthenticated = createAction('app/appAuthenticated');
export const appUnauthenticated = createAction('app/appUnauthenticated');
export const appAuthenticatedTransitionEnd = createAction(
  'app/appAuthenticatedTransitionEnd',
);
export const appUnauthenticatedTransitionEnd = createAction(
  'app/appUnauthenticatedTransitionEnd',
);
export const appLogout = createAction('app/appLogout');
export const displayLastLaunchedGameToast = createAction(
  'app/displayLastLaunchedGameToast',
);
export const appActive = createAction<AppStateStatus>('app/appActive');
export const appInactive = createAction<AppStateStatus>('app/appInactive');
export const appInBackground = createAction<AppStateStatus>(
  'app/appInBackground',
);
export const appLock = createAction('app/appLock');
export const appDidCatchError = createAction<unknown>('app/didCatchError');
export const socketConnected = createAction<HubConnection>(
  'app/socketConnected',
);
export const socketDisconnected = createAction('app/socketDisconnected');
export const networkStateOffline = createAction('app/networkStateOffline');
export const networkStateOnline = createAction('app/networkStateOnline');
export const appSync = createAction('app/appSync');
export const appUnsync = createAction('app/appUnsync');
export const setLiveChatState = createAction('app/setLiveChatState');
export const accountStatusUpdated = createAction('app/accountStatusUpdated');
export const verifyIdentity = createAction('app/verifyIdentity');
export const registerUser =
  createAction<RegisterUserPayload>('app/registerUser');
export const loginUser = createAction('app/loginUser');

export const setFirstName =
  createAction<SetFirstNamePayload>('app/setFirstName');

export const initiateLogin = createAction('app/initiateLogin');

export const pushNotificationClick = createAction<PushNotificationClick>(
  'app/pushNotificationClick',
);

export const pushNotificationImpression =
  createAction<PushNotificationImpression>('app/pushNotificationImpression');

const initialState: AppState = {
  appState: 'active',
  networkState: 'online',
  authenticationStatus: 'unauthenticated',
  activityStarted: false,
  popupsEnabled: true,
};

const app = createSlice({
  name: 'app',
  initialState,
  reducers: {
    startActivity(state) {
      state.activityStarted = true;
    },
    enablePopups(state) {
      state.popupsEnabled = true;
    },
    disablePopups(state) {
      state.popupsEnabled = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(appActive, (state) => {
      state.appState = 'active';
      state.activityStarted = false;

      return state;
    });

    builder.addCase(appInactive, (state, action) => {
      if (action.payload === 'active') {
        state.appState = 'active';
        state.activityStarted = false;

        return state;
      }

      state.appState = 'inactive';

      return state;
    });

    builder.addCase(appInBackground, (state) => {
      state.appState = 'background';

      return state;
    });

    builder.addCase(appLock, (state) => {
      state.appState = 'appLock';
    });

    builder.addCase(networkStateOffline, (state) => {
      state.networkState = 'offline';

      return state;
    });

    builder.addCase(networkStateOnline, (state) => {
      state.networkState = 'online';

      return state;
    });

    builder.addCase(appSync, (state) => {
      state.authenticationStatus = 'authenticated';
    });

    builder.addCase(appLogout, (state) => {
      state.authenticationStatus = 'unauthenticated';
    });
  },
});

export const { startActivity, disablePopups, enablePopups } = app.actions;

export const selectAppState = (state: State) => state.app.appState;
export const selectNetworkState = (state: State) => state.app.networkState;
export const selectAuthenticationStatus = (state: State) =>
  state.app.authenticationStatus;
export const selectActivityStarted = (state: State) =>
  state.app.activityStarted;

const persistConfig = {
  key: 'app',
  storage: AsyncStorage,
  whitelist: ['authenticationStatus'],
};

const persistedReducer = persistReducer(persistConfig, app.reducer);

export default persistedReducer;
